import { Select, SelectProps } from '@shopify/polaris'
import useSWR from 'swr'

import { backendFetchResultData } from './api'
import { ExternalAdminLink, gidToId } from './helpers'
import { urlWithParams } from './urls'

export const windowWithSelectedThemeIdCache = window as unknown as {
  paywhirlSelectedThemeId: number | null
}

windowWithSelectedThemeIdCache.paywhirlSelectedThemeId = null

export interface Theme {
  id: number
  name: string
  role: string
  theme_store_id: number | null
  title: string
  supports_plan_selector_block: boolean | null
}

export const useThemesSWR = () =>
  useSWR<Theme[]>(['shop-themes'], () => backendFetchResultData('GET', '/shop/themes'))

export const useThemeSWR = (themeId: number | null) =>
  useSWR<Theme | null>(
    ['shop-theme', themeId],
    () => (themeId ? backendFetchResultData('GET', `/shop/themes/${themeId}`) : null),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

export interface ThemeSettings {
  theme: {
    id: string
    name: string
  } | null
  themeSupportsAppBlocks: boolean
  customerPortalInstalled: boolean
  subscriptionWidgetInstalled: boolean
}

export const mainThemeId = (settings: ThemeSettings | undefined): string =>
  settings?.theme ? gidToId(settings.theme.id) : 'current'

export const useMainThemeSettingsSWR = () =>
  useSWR<ThemeSettings>(['shop-main-theme-settings'], () =>
    backendFetchResultData('GET', '/shop/themes/main/settings')
  )

interface ThemeSelectProps extends Omit<SelectProps, 'options' | 'value' | 'onChange'> {
  themes: Theme[]
  onChange: (theme: Theme) => void
  value: Theme
}

export const ThemeSelect = (props: ThemeSelectProps) => {
  const { themes, onChange, value, ...selectProps } = props

  return (
    <Select
      options={themes.map((t, i) => ({ label: t.title, value: i.toString() }))}
      onChange={(val) => {
        const theme = props.themes[parseInt(val)]
        windowWithSelectedThemeIdCache.paywhirlSelectedThemeId = theme.id
        props.onChange(theme)
      }}
      value={themes.indexOf(value).toString()}
      {...selectProps}
    />
  )
}

export const ThemeLink = ({ id, path, children }: { id: number; path: string; children: any }) => (
  <ExternalAdminLink url={`/themes/${id}?key=${encodeURIComponent(path)}`} children={children} />
)

export const ThemeEditorLink = ({
  id,
  previewPath = undefined,
  context = undefined,
  children,
}: {
  id: number
  previewPath?: string
  context?: 'apps'
  children: any
}) => {
  const url = urlWithParams(`/themes/${id}/editor`, { previewPath, context })
  return <ExternalAdminLink url={url} children={children} />
}
